import { fabric } from 'fabric';
import { CUIColors } from '@dotcom/crown-ui';
import { CardContextState } from '../../../context/card-context';
import { CardEditableArea, CardFaceData } from '../../../global-types';
import { getRegionalConfig } from '../../../regional-config/get-regional-config';
import colorsList from '../../../styles/util.scss';
import { CanvasDataTypes } from '../../../utils';
import { getEditableAreaSettings } from '../card-editor-utils';
import {
  getUserZonePhotoButton,
  getUserZoneTextButton,
  getUserZoneWamButton,
  getUserZoneInstructions,
} from './get-editable-area-buttons';
import { scaleEditableAreaButton } from './scale-editable-area-buttons';
import type { EditorPresentation } from '../../../features/edit-history/edit-history';

/**
 * Adds editable user zones and waits for all their image assets to be inserted.
 */
export const addEditableAreas = (
  face: CardFaceData,
  cardState: CardContextState,
  addButtonsOnly?: boolean,
  isOneToMany?: boolean,
  presentation?: EditorPresentation,
): Promise<void> => {
  const canvas = face?.canvas?.current;
  if (!canvas) {
    return Promise.resolve();
  }

  const { userZones, features } = getRegionalConfig();
  const shouldShowPodWam = (face.type !== 'front' && features?.isPodWamEnabledOnlyInside) || features?.isPodWamEnabled;
  const { editableAreas } = face;
  if (!editableAreas?.length) {
    return Promise.resolve();
  }

  const hasAreaObjects = Boolean(face.originalCanvasJson?.objects?.some((object) => Boolean(object.clipPath)));
  const pendingAssetOperations: Promise<void>[] = [];
  const objects = canvas.getObjects() ?? [];

  editableAreas.forEach((area: CardEditableArea, index: number) => {
    const areaSettings = getEditableAreaSettings(area, index, cardState);
    const canvasArea = new fabric.Rect(areaSettings);
    const historicalState = (type: string) =>
      presentation?.find(
        (helper) =>
          helper.type === type && (helper.photoZoneId === areaSettings.name || helper.name === areaSettings.name),
      );
    const shouldInclude = (type: string) => presentation === undefined || historicalState(type) !== undefined;
    const addHelper = (object: fabric.Object, type: string): void => {
      const state = historicalState(type);
      if (presentation !== undefined && !state) {
        return;
      }
      if (state) {
        object.set({ visible: state.visible });
      }
      canvas.add(object);
    };
    const strokedMarginElement = new fabric.Rect({
      ...canvasArea,
      stroke: colorsList[CUIColors.EditorBackground],
      strokeDashArray: [18, 18],
      data: { type: CanvasDataTypes.StrokedMargin },
    });

    const editableAreaObject = objects.find(
      (element) => element.data?.type === CanvasDataTypes.EditableArea && element.name === areaSettings.name,
    );
    const zone = editableAreaObject ?? canvasArea;
    if (!editableAreaObject) {
      addHelper(strokedMarginElement, CanvasDataTypes.StrokedMargin);
      addHelper(canvasArea, CanvasDataTypes.EditableArea);
      if (shouldInclude(CanvasDataTypes.EditableArea)) {
        canvasArea.moveTo(0);
      }
    } else {
      editableAreaObject.set({ selectable: false, evented: false });
      editableAreaObject.moveTo(0);
    }

    if (presentation === undefined && hasAreaObjects && !addButtonsOnly) {
      return;
    }

    const existingZoneObjects = objects.filter((object) => object.data?.photoZoneId === zone.name);
    const hasExistingType = (type: string) => existingZoneObjects.some((object) => object.data?.type === type);
    if (shouldInclude(CanvasDataTypes.PhotoZoneButton) && !hasExistingType(CanvasDataTypes.PhotoZoneButton)) {
      pendingAssetOperations.push(
        getUserZonePhotoButton(zone, isOneToMany ?? false).then((button) => {
          addHelper(scaleEditableAreaButton(button, zone), CanvasDataTypes.PhotoZoneButton);
        }),
      );
    }

    if (
      shouldInclude(CanvasDataTypes.UserZoneAddTextButton) &&
      !hasExistingType(CanvasDataTypes.UserZoneAddTextButton)
    ) {
      pendingAssetOperations.push(
        getUserZoneTextButton(zone, isOneToMany ?? false).then((button) => {
          addHelper(scaleEditableAreaButton(button, zone), CanvasDataTypes.UserZoneAddTextButton);
        }),
      );
    }

    const includeWam =
      presentation === undefined
        ? shouldShowPodWam && !isOneToMany
        : shouldInclude(CanvasDataTypes.UserZoneAddWamButton);
    if (includeWam && !hasExistingType(CanvasDataTypes.UserZoneAddWamButton)) {
      pendingAssetOperations.push(
        getUserZoneWamButton(zone).then((button) => {
          addHelper(scaleEditableAreaButton(button, zone), CanvasDataTypes.UserZoneAddWamButton);
        }),
      );
    }

    const includeInstructions =
      presentation === undefined
        ? userZones.areInstructionsEnabled
        : shouldInclude(CanvasDataTypes.UserZoneInstructions);
    if (includeInstructions && !hasExistingType(CanvasDataTypes.UserZoneInstructions)) {
      pendingAssetOperations.push(
        getUserZoneInstructions(zone).then((instructions) => {
          addHelper(instructions, CanvasDataTypes.UserZoneInstructions);
        }),
      );
    }
  });

  return Promise.allSettled(pendingAssetOperations).then((results) => {
    const failedOperation = results.find((result) => result.status === 'rejected');
    if (failedOperation?.status === 'rejected') {
      throw failedOperation.reason;
    }
  });
};
